<template>
  <div class="fixed inset-0 z-[100] bg-[#F8FAFC] flex flex-col animate-fade-in">
    <div class="h-16 border-b border-slate-200 flex items-center px-8 justify-between bg-white">
      <div class="flex items-center gap-4">
        <button @click="onExit" class="text-slate-400 hover:text-slate-600 transition-all text-xs font-bold uppercase tracking-widest flex items-center gap-2">
          <IconRefreshCw class="w-4 h-4" /> 退出演练
        </button>
        <div class="h-4 w-px bg-slate-200"></div>
        <span class="text-slate-400 text-xs font-bold uppercase tracking-[0.2em]">实战演练：进行中</span>
      </div>
    </div>

    <div class="flex-1 flex overflow-hidden">
      <div class="w-1/2 p-12 border-r border-slate-200 overflow-y-auto">
        <div class="max-w-xl mx-auto space-y-8">
          <div class="text-slate-800 text-lg leading-loose font-medium">
            {{ scenario.description }}
          </div>
        </div>
      </div>

      <div class="w-1/2 bg-white p-12 overflow-y-auto">
        <div class="max-w-xl mx-auto">
          <div v-if="!isSubmitted" class="space-y-10">
            <h3 class="text-xl font-black text-slate-900 tracking-tight">战术选择</h3>
            <div class="space-y-4">
              <button
                v-for="option in scenario.options"
                :key="option.id"
                @click="selectedId = option.id"
                class="w-full text-left p-6 rounded-2xl border-2 transition-all group"
                :class="selectedId === option.id
                  ? 'border-[#F2C94C] bg-[#F2C94C]/5'
                  : 'border-slate-100 bg-slate-50 hover:border-slate-200'"
              >
                <div class="flex items-center gap-4">
                  <div class="w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all"
                    :class="selectedId === option.id ? 'border-[#F2C94C] bg-[#F2C94C]' : 'border-slate-300'"
                  >
                    <div v-if="selectedId === option.id" class="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                  <span class="text-sm font-bold transition-all"
                    :class="selectedId === option.id ? 'text-slate-900' : 'text-slate-400 group-hover:text-slate-600'"
                  >
                    {{ option.text }}
                  </span>
                </div>
              </button>
            </div>
            <button
              :disabled="!selectedId"
              @click="isSubmitted = true"
              class="w-full py-6 bg-[#F2C94C] disabled:bg-slate-100 disabled:text-slate-300 text-white font-black rounded-2xl shadow-xl transition-all active:scale-[0.98] uppercase tracking-widest"
            >
              {{ selectedId ? '确认提交' : '先选择战术' }}
            </button>
          </div>

          <div v-else class="space-y-12 animate-slide-up">
            <div class="flex items-center gap-6">
              <div v-if="currentOption && currentOption.isError" class="w-20 h-20 bg-red-50 rounded-3xl flex items-center justify-center border border-red-100">
                <IconShieldAlert class="w-10 h-10 text-red-500" />
              </div>
              <div v-else class="w-20 h-20 bg-green-50 rounded-3xl flex items-center justify-center border border-green-100">
                <IconCircleCheck class="w-10 h-10 text-green-500" />
              </div>
              <div>
                <h3 class="text-2xl font-black"
                  :class="currentOption && currentOption.isError ? 'text-red-500' : 'text-green-500'"
                >
                  {{ currentOption && currentOption.isError ? '决策判定：红区警告' : '决策判定：推演成功' }}
                </h3>
              </div>
            </div>

            <div class="space-y-4">
              <h5 class="text-[10px] font-black text-slate-400 uppercase tracking-widest">深度战术解析</h5>
              <div class="text-slate-700 leading-loose bg-slate-50 p-6 rounded-2xl border border-slate-100 text-sm">
                {{ currentOption && (currentOption.feedback || currentOption.consequence) }}
              </div>
            </div>

            <div class="flex gap-3">
              <button
                @click="onExit"
                class="flex-1 py-4 border-2 border-slate-200 text-slate-500 font-black rounded-2xl hover:bg-slate-50 transition-all"
              >
                退出演练
              </button>
              <button
                @click="handleBookmark"
                class="flex-1 py-4 border-2 font-black rounded-2xl transition-all flex items-center justify-center gap-2"
                :class="isBookmarked
                  ? 'border-[#F2C94C] text-[#F2C94C] bg-[#F2C94C]/5'
                  : 'border-slate-200 text-slate-500 hover:bg-slate-50'"
              >
                <IconStar class="w-4 h-4" :class="{ 'fill-[#F2C94C]': isBookmarked }" />
                {{ isBookmarked ? '已收藏' : '收藏题目' }}
              </button>
              <button
                @click="onNext"
                class="flex-1 py-4 bg-[#F2C94C] text-white font-black rounded-2xl hover:bg-[#E5B73B] transition-all"
              >
                进入下一关
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { IconShieldAlert, IconCircleCheck, IconRefreshCw, IconStar } from '../icons'

export default {
  name: 'SimulationEngine',
  components: {
    IconShieldAlert,
    IconCircleCheck,
    IconRefreshCw,
    IconStar
  },
  props: {
    scenario: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedId: null,
      isSubmitted: false,
      isBookmarked: false
    }
  },
  computed: {
    currentOption() {
      return this.scenario.options.find(o => o.id === this.selectedId)
    }
  },
  methods: {
    handleBookmark() {
      this.isBookmarked = !this.isBookmarked
      const bookmarks = JSON.parse(localStorage.getItem('simulation_bookmarks') || '[]')
      if (this.isBookmarked) {
        bookmarks.push({
          id: this.scenario.id,
          description: this.scenario.description,
          selectedOption: this.currentOption ? this.currentOption.text : '',
          result: this.currentOption && this.currentOption.isError ? 'warning' : 'success',
          bookmarkedAt: Date.now()
        })
      } else {
        const index = bookmarks.findIndex(b => b.id === this.scenario.id)
        if (index > -1) bookmarks.splice(index, 1)
      }
      localStorage.setItem('simulation_bookmarks', JSON.stringify(bookmarks))
    },
    onExit() {
      this.$emit('exit')
    },
    onNext() {
      this.$emit('next')
    }
  }
}
</script>
